import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSelector from "./LanguageSelector";
import LeistungenMenu from "./LeistungenMenu";

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const navItems = [
    { name: t("navigation.home"), href: "/", section: "#home" },
    { name: t("navigation.expertise"), href: "/", section: "#expertise" },
    { name: t("navigation.insights"), route: "/insights" },
    { name: t("navigation.tools"), route: "/tools" },
    { name: t("navigation.media"), href: "/", section: "#media" },
    { name: t("navigation.ventures"), href: "/", section: "#ventures" },
    { name: t("navigation.contact"), href: "/", section: "#contact" },
  ];

  // Handle scroll to section after navigation
  useEffect(() => {
    if (location.pathname === "/" && location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          const navHeight = 80;
          const elementPosition = element.offsetTop - navHeight;
          window.scrollTo({
            top: elementPosition,
            behavior: "smooth",
          });
        }, 100); // Small delay to ensure page is loaded
      }
    }
  }, [location]);

  const handleNavigation = (item) => {
    // Real-route items (e.g. Insights) navigate instead of scrolling
    if (item.route) {
      navigate(item.route);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setIsOpen(false);
      return;
    }
    if (location.pathname === "/") {
      // On home page, scroll to section and update URL hash
      const element = document.querySelector(item.section);
      if (element) {
        const navHeight = 80; // Height of the fixed navigation
        const elementPosition = element.offsetTop - navHeight;

        window.scrollTo({
          top: elementPosition,
          behavior: "smooth",
        });

        // Update URL hash to reflect current section
        window.history.pushState(null, null, item.section);
      }
    } else {
      // On other pages, navigate to home page with section
      navigate(item.href + item.section);
    }
    setIsOpen(false);
  };

  const handleLogoClick = () => {
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      // If already on home page, scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleKeyDown = (event, item) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleNavigation(item);
    }
  };

  const handleMenuKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsOpen(!isOpen);
    } else if (event.key === "Escape" && isOpen) {
      setIsOpen(false);
    }
  };

  return (
    <nav
      className="fixed w-full  z-50 transition-all duration-300 bg-[#0F1B2D]/95 backdrop-blur-md shadow-luxury"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="container  mx-auto px-6">
        <div className="flex justify-between items-center py-4">
          {/* Logo/Brand */}
          <div
            className="flex items-center cursor-pointer"
            onClick={handleLogoClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleLogoClick();
              }
            }}
            tabIndex={0}
            role="button"
            aria-label="Go to homepage"
          >
            <div
              className="w-12 h-12 bg-gradient-to-br from-[#C5A66A] to-[#d4b886] rounded-lg flex items-center justify-center mr-3"
              aria-hidden="true"
            >
              <span className="text-[#0F1B2D] font-bold text-xl font-playfair">
                DK
              </span>
            </div>
            <div className="text-white">
              <div className="font-playfair font-bold text-xl">
                Daaniyal Khan
              </div>
              <div className="text-[#C5A66A] text-sm">Strategic Consulting</div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div
            className="hidden xl:flex items-center space-x-6 lg:space-x-4"
            role="menubar"
          >
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigation(item)}
                onKeyDown={(e) => handleKeyDown(e, item)}
                className="text-white cursor-pointer hover:text-[#C5A66A] hover:scale-105 transition-colors duration-300 font-medium text-sm lg:text-base"
                role="menuitem"
                aria-label={`Navigate to ${item.name} section`}
              >
                {item.name}
              </button>
            ))}
            <LeistungenMenu onNavigate={() => setIsOpen(false)} />
            <LanguageSelector onLanguageChange={() => setIsOpen(false)} />
            <button
              onClick={() =>
                handleNavigation({ href: "/", section: "#contact" })
              }
              onKeyDown={(e) =>
                handleKeyDown(e, { href: "/", section: "#contact" })
              }
              className="bg-[#C5A66A] cursor-pointer text-[#0F1B2D] px-4 lg:px-6 py-2 rounded-lg font-semibold hover:bg-[#b8966b] transition-all duration-300 transform hover:scale-105 text-sm lg:text-base"
              aria-label="Book strategy session"
            >
              {t("navigation.bookStrategy")}
            </button>
          </div>

          {/* Medium Desktop Navigation (1024-1279px) */}
          <div
            className="hidden lg:flex xl:hidden items-center space-x-4"
            role="menubar"
          >
            {navItems.slice(0, 3).map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigation(item)}
                onKeyDown={(e) => handleKeyDown(e, item)}
                className="text-white cursor-pointer hover:text-[#C5A66A] hover:scale-105 transition-colors duration-300 font-medium text-sm"
                role="menuitem"
                aria-label={`Navigate to ${item.name} section`}
              >
                {item.name}
              </button>
            ))}
            <LanguageSelector onLanguageChange={() => setIsOpen(false)} />
            <button
              onClick={() =>
                handleNavigation({ href: "/", section: "#contact" })
              }
              onKeyDown={(e) =>
                handleKeyDown(e, { href: "/", section: "#contact" })
              }
              className="bg-[#C5A66A] cursor-pointer text-[#0F1B2D] px-3 py-2 rounded-lg font-semibold hover:bg-[#b8966b] transition-all duration-300 transform hover:scale-105 text-sm"
              aria-label="Book strategy session"
            >
              {t("navigation.bookStrategy")}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              onKeyDown={handleMenuKeyDown}
              className="text-white focus:outline-none"
              aria-expanded={isOpen}
              aria-label={isOpen ? "Close menu" : "Open menu"}
              aria-controls="mobile-menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                {isOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          id="mobile-menu"
          className={`lg:hidden  transition-all duration-300 ease-in-out ${
            isOpen
              ? "h-auto mb-4 opacity-100"
              : "max-h-0 opacity-0 overflow-hidden"
          }`}
          role="menu"
          aria-hidden={!isOpen}
        >
          <div className="py-4  space-y-2 bg-[#1a2b42] rounded-lg mt-2">
            <div className="px-6 py-3">
              <div className="relative">
                <LanguageSelector onLanguageChange={() => setIsOpen(false)} />
              </div>
            </div>
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigation(item)}
                onKeyDown={(e) => handleKeyDown(e, item)}
                className="block w-full text-left px-6 py-3 text-white hover:bg-[#C5A66A] hover:text-[#0F1B2D] transition-colors duration-300"
                role="menuitem"
                aria-label={`Navigate to ${item.name} section`}
              >
                {item.name}
              </button>
            ))}
            <div className="px-6 pt-3 pb-1 text-xs uppercase tracking-wider text-[#C5A66A] font-semibold">
              {t("navigation.leistungen")}
            </div>
            {[
              { label: t("services.services.kiVertrieb.title"), route: "/ki-vertrieb" },
              { label: t("services.services.kiVersicherung.title"), route: "/ki-versicherung" },
              { label: t("services.services.dashboards.title"), route: "/management-dashboards" },
              { label: t("navigation.buecher"), route: "/buecher" },
            ].map((p) => (
              <button
                key={p.route}
                onClick={() => handleNavigation({ route: p.route })}
                onKeyDown={(e) => handleKeyDown(e, { route: p.route })}
                className="block w-full text-left px-6 py-3 text-white hover:bg-[#C5A66A] hover:text-[#0F1B2D] transition-colors duration-300"
                role="menuitem"
              >
                {p.label}
              </button>
            ))}
            <div className="px-6 py-3">
              <button
                onClick={() =>
                  handleNavigation({ href: "/", section: "#contact" })
                }
                onKeyDown={(e) =>
                  handleKeyDown(e, { href: "/", section: "#contact" })
                }
                className="w-full bg-[#C5A66A] text-[#0F1B2D] px-6 py-3 rounded-lg font-semibold hover:bg-[#b8966b] transition-all duration-300"
                aria-label="Book strategy session"
              >
                {t("navigation.bookStrategy")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
